type TriggerConfig = {
  isInactivityModalEnabled: boolean;
  isQuickSwipeUpModalEnabled: boolean;
  isTimeOnPageEnabled?: boolean;
  isExitIntentEnabled?: boolean;
  isScrollDepthModalEnabled?: boolean;
  isPurchasedDaysAgoModalEnabled?: boolean;
  isUserActionTriggerEnabled?: boolean;
  isLocalTimeTriggerEnabled?: boolean;

  triggerMode: "and" | "or";
  hideModalExpireTime?: string;
  frequencyType?: string;
  frequencyValue?: number;
  minCartItemCount?: number;
};

export type TriggerCompatibilityRule = {
  id: string;
  validate: (config: TriggerConfig) => string | null;
};

// Only event-like triggers that can actively fire modal opening attempts belong here. Intentionally excluded:
// - isPurchasedDaysAgoModalEnabled: eligibility gate, not an event trigger
// - isLocalTimeTriggerEnabled: time-window gate, not an event trigger
const TRIGGER_KEYS = [
  "isInactivityModalEnabled",
  "isQuickSwipeUpModalEnabled",
  "isTimeOnPageEnabled",
  "isExitIntentEnabled",
  "isScrollDepthModalEnabled",
  "isUserActionTriggerEnabled",
] as const satisfies ReadonlyArray<keyof TriggerConfig>;

const TRIGGER_LABELS = "Inactivity, Quick Swipe Up, Time on Page, Exit Intent, Scroll Depth, or User Action";

const countEnabledTriggers = (config: TriggerConfig) => TRIGGER_KEYS.filter((key) => Boolean(config[key])).length;

export const TRIGGER_COMPATIBILITY_RULES: TriggerCompatibilityRule[] = [
  {
    id: "at-least-one-trigger-enabled",
    validate: (config) =>
      countEnabledTriggers(config) === 0 ? `At least one trigger must be enabled (${TRIGGER_LABELS}).` : null,
  },
  {
    id: "and-mode-requires-multiple-triggers",
    validate: (config) =>
      config.triggerMode === "and" && countEnabledTriggers(config) < 2
        ? 'Trigger mode "and" requires at least two enabled triggers. Enable a second trigger or switch mode to "or".'
        : null,
  },
  {
    id: "swipe-up-requires-short-expire-time",
    validate: ({ isQuickSwipeUpModalEnabled, frequencyType, frequencyValue, hideModalExpireTime }) => {
      if (!isQuickSwipeUpModalEnabled) return null;

      if (frequencyType === "everyXDays" && frequencyValue !== undefined && frequencyValue > 3) {
        return "Quick Swipe Up is a high-frequency gesture. Frequency value above 3 days may cause the modal to feel intrusive. Consider setting it to 1–3 days.";
      }

      if (frequencyType === undefined && hideModalExpireTime !== undefined) {
        const days = Number(hideModalExpireTime);
        if (days > 3) {
          return 'Quick Swipe Up is a high-frequency gesture. "Hide modal expire time" above 3 days may cause the modal to feel intrusive. Consider setting it to 1–3 days.';
        }
      }

      return null;
    },
  },
];

/**
 * Runs all compatibility rules against the given config.
 * Returns an array of violation messages (empty when all rules pass).
 */
export function validateTriggerCompatibility(config: TriggerConfig): string[] {
  return TRIGGER_COMPATIBILITY_RULES.flatMap((rule) => {
    const message = rule.validate(config);
    return message !== null ? [message] : [];
  });
}
